import { Link } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import type { CityPageContent } from "@/data/city-page-content";
import { getPostBySlug } from "@/data/blog-posts";

type Props = { cityName: string; content: CityPageContent };

/**
 * City-specific body copy rendered inside LocationPageTemplate's intro slot.
 * Layout is shared; every word comes from src/data/city-page-content.ts so the
 * pages are not near-duplicates of each other.
 */
export function CityLocationIntro({ cityName, content }: Props) {
  const posts = content.blogSlugs
    .map((slug) => getPostBySlug(slug))
    .filter((p): p is NonNullable<ReturnType<typeof getPostBySlug>> => Boolean(p));

  return (
    <div className="space-y-6">
      <p>{content.context}</p>
      <p>{content.proximity}</p>

      {/* CTA 1 */}
      <div className="rounded-xl border-l-4 border-sky-500 bg-sky-50 p-6">
        <p className="text-[#0b0a3d] font-bold text-lg">
          Need help with something today in {cityName}?
        </p>
        <p className="mt-2 text-slate-700">
          Call{" "}
          <a
            href="tel:+19092559112"
            className="inline-flex items-center gap-2 text-sky-700 font-bold underline hover:text-sky-800"
          >
            <Phone className="w-4 h-4" aria-hidden="true" />
            (909) 255-9112
          </a>{" "}
          and you'll reach a local technician, or{" "}
          <Link to="/contact" className="text-sky-700 font-semibold underline hover:text-sky-800">
            send us a message
          </Link>
          .
        </p>
      </div>

      <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b0a3d] pt-4">
        What We Cover for {cityName} Businesses
      </h2>

      <div className="space-y-5">
        <div>
          <h3 className="text-lg font-bold text-[#0b0a3d]">Managed IT</h3>
          <p className="mt-2">{content.services.managedIt}</p>
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#0b0a3d]">Cybersecurity</h3>
          <p className="mt-2">{content.services.cybersecurity}</p>
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#0b0a3d]">VoIP Phone Systems</h3>
          <p className="mt-2">{content.services.voip}</p>
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#0b0a3d]">Tech Support</h3>
          <p className="mt-2">{content.services.techSupport}</p>
        </div>
      </div>

      <h2 className="text-2xl md:text-3xl font-extrabold text-[#0b0a3d] pt-4">
        Why a Local Inland Empire Team Matters
      </h2>
      <p>{content.whyLocal}</p>

      <div className="pt-2">
        <p className="text-sm font-bold tracking-wider uppercase text-slate-500">
          Explore these services
        </p>
        <ul className="mt-3 grid sm:grid-cols-2 gap-2">
          {content.serviceLinks.map((s) => (
            <li key={s.href}>
              <a href={s.href} className="text-sky-700 font-semibold underline hover:text-sky-800">
                {s.label}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {posts.length > 0 && (
        <div className="pt-2">
          <p className="text-sm font-bold tracking-wider uppercase text-slate-500">
            Further reading for {cityName}
          </p>
          <ul className="mt-3 space-y-2 list-disc pl-5">
            {posts.map((post) => (
              <li key={post.slug}>
                <Link
                  to="/articles/$slug"
                  params={{ slug: post.slug }}
                  className="text-sky-700 font-semibold underline hover:text-sky-800"
                >
                  {post.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
